import { _ as _object_spread } from "@swc/helpers/_/_object_spread";
import { _ as _object_spread_props } from "@swc/helpers/_/_object_spread_props";
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import React, { useMemo, useRef, useState } from 'react';
import cn from 'classnames';
import { format as formatDate } from 'date-fns';
import { ColorCalendar, ColorCalendarError } from '../../icons';
import { useBiAction } from '../BiTracking';
import { DateCalendar, violatedDateBound } from '../DatePicker';
import { PickerLocalization } from '../DatePicker/PickerLocalization';
import { Popover } from '../Popover';
import { Spinner } from '../Spinner';
import { Tooltip } from '../Tooltip';
import styles from './InlineSelectDatePicker.module.scss';
import { GetInlineSelectDatePickerTID, inlineSelectDatePickerTriggerColor, toDate } from './InlineSelectDatePicker.utils';
export function InlineSelectDatePicker(props) {
    var _ref, _ref1;
    const { loading, disabled, disableErrors, size = 'medium', onChange, highlightDay, helperText, skin = 'primary', format: formatProp, defaultOpen = false, testId, minWidth, minDate, maxDate, shouldDisableDate, headerFormat, clearable, clearLabel, error, value, emptyLabel, className, PopoverProps, flagValueOutOfBounds = true, bi } = props;
    const { InlineSelectDatePickerTestIds } = useMemo(()=>GetInlineSelectDatePickerTID(testId), [
        testId
    ]);
    const logBiAction = useBiAction();
    const anchorRef = useRef(null);
    // WHY: anchored calendar popover, not a centered dialog — OverlayContext.showModal / DeclarativeModal don't apply.
    // eslint-disable-next-line no-restricted-syntax
    const [open, setOpen] = useState(defaultOpen);
    // WHY: forward the picker's own bi/noBi to the Clear action — when the consumer opts out, so does Clear.
    // eslint-disable-next-line custom/no-hardcoded-nobi-in-seagull
    const forwardedBiProps = props.bi != null ? {
        bi: props.bi
    } : {
        noBi: true
    };
    const isDisabled = !!disabled || !!loading;
    const format = formatProp || 'MMM dd, yyyy';
    const dateValue = toDate(value);
    const valueInvalid = flagValueOutOfBounds && dateValue != null && (violatedDateBound({
        value: dateValue,
        minDate,
        maxDate
    }) != null || ((_ref = shouldDisableDate === null || shouldDisableDate === void 0 ? void 0 : shouldDisableDate(dateValue)) !== null && _ref !== void 0 ? _ref : false));
    const showError = (!!error || valueInvalid) && !disableErrors;
    const displayValue = dateValue ? formatDate(dateValue, format) : emptyLabel !== null && emptyLabel !== void 0 ? emptyLabel : '';
    const close = ()=>setOpen(false);
    const openPicker = ()=>!isDisabled && setOpen(true);
    const handlePick = (date)=>{
        close();
        if (date && (bi === null || bi === void 0 ? void 0 : bi.name)) {
            logBiAction({
                entityType: 'date_picker',
                entityName: bi.name,
                actionType: 'select',
                data: _object_spread_props(_object_spread({}, bi.data), {
                    test_id: testId !== null && testId !== void 0 ? testId : '',
                    selected_value: date.toISOString().split('T')[0]
                })
            });
        }
        onChange(date, date ? formatDate(date, format) : null);
    };
    const actions = clearable && dateValue != null ? [
        _object_spread({
            label: clearLabel !== null && clearLabel !== void 0 ? clearLabel : 'Clear date',
            testId: InlineSelectDatePickerTestIds.Clear(testId),
            onClick: ()=>{
                close();
                onChange(null);
            }
        }, forwardedBiProps)
    ] : undefined;
    const color = inlineSelectDatePickerTriggerColor({
        skin,
        showError,
        isDisabled
    });
    const trigger = /*#__PURE__*/ _jsxs("span", {
        ref: anchorRef,
        className: cn(styles.root, className),
        "data-testid": testId,
        style: minWidth != null ? {
            minWidth
        } : undefined,
        children: [
            /*#__PURE__*/ _jsxs("span", {
                role: "button",
                tabIndex: isDisabled ? -1 : 0,
                "data-testid": InlineSelectDatePickerTestIds.Btn(testId),
                "data-error": showError ? 'true' : undefined,
                "data-disabled": isDisabled ? 'true' : undefined,
                "aria-disabled": isDisabled,
                className: cn(styles.trigger, {
                    [styles.triggerSmall]: size === 'small',
                    [styles.triggerDisabled]: isDisabled
                }),
                onClick: openPicker,
                onKeyDown: (event)=>{
                    if (event.key === 'Enter' || event.key === ' ') {
                        event.preventDefault();
                        openPicker();
                    }
                },
                children: [
                    /*#__PURE__*/ _jsx("span", {
                        className: styles.value,
                        style: {
                            color
                        },
                        "aria-invalid": showError,
                        "data-testid": InlineSelectDatePickerTestIds.Input(testId),
                        children: displayValue
                    }),
                    /*#__PURE__*/ _jsx("span", {
                        className: styles.icon,
                        style: isDisabled ? {
                            opacity: 0.4
                        } : undefined,
                        children: loading ? /*#__PURE__*/ _jsx(Spinner, {
                            size: 16,
                            testId: testId && "".concat(testId, "-loading")
                        }) : showError ? /*#__PURE__*/ _jsx(ColorCalendarError, {}) : /*#__PURE__*/ _jsx(ColorCalendar, {})
                    })
                ]
            }),
            /*#__PURE__*/ _jsx(Popover, _object_spread_props(_object_spread({}, PopoverProps), {
                open: open && !isDisabled,
                anchorEl: anchorRef.current,
                onClose: close,
                anchorOrigin: (_ref1 = PopoverProps === null || PopoverProps === void 0 ? void 0 : PopoverProps.anchorOrigin) !== null && _ref1 !== void 0 ? _ref1 : {
                    vertical: 'bottom',
                    horizontal: 'left'
                },
                testId: InlineSelectDatePickerTestIds.Popover(testId),
                children: /*#__PURE__*/ _jsx(PickerLocalization, {
                    children: /*#__PURE__*/ _jsx(DateCalendar, {
                        value: dateValue,
                        onChange: handlePick,
                        minDate: minDate,
                        maxDate: maxDate,
                        shouldDisableDate: shouldDisableDate,
                        highlightDay: highlightDay,
                        actions: actions,
                        headerFormat: headerFormat,
                        testId: testId
                    })
                })
            }))
        ]
    });
    const tooltipText = disableErrors ? undefined : helperText;
    if (!tooltipText) return trigger;
    return /*#__PURE__*/ _jsx(Tooltip, {
        placement: "top",
        title: open ? '' : tooltipText,
        testId: InlineSelectDatePickerTestIds.Tooltip(testId),
        children: trigger
    });
}
